import { Controller } from '@hotwired/stimulus'
import * as Turbo from '@hotwired/turbo'
import {
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift
} from '@floating-ui/dom'

const OFFSET_PX = 8
const TURBO_STREAM = 'text/vnd.turbo-stream.html'
const REQUEST_TIMEOUT_MS = 15000

export default class extends Controller {
  static targets = [
    'panel',
    'anchor',
    'roomId', 'nameInput', 'surfaceInput',
    'nameError', 'surfaceError',
    'deleteButton',
    'recutError'
  ]

  static values = {
    simulationRequestSqid: String,
    nameRequired: String,
    nameTooLong: String,
    surfaceRequired: String,
    requestFailed: String,
    roomsCount: Number
  }

  #pending = false
  #lockWrapper = null

  connect () {
    this.referenceElement = null
    this.cleanupAutoUpdate = null
  }

  disconnect () {
    this.#teardownPositioning()
    document.removeEventListener('click', this.#onClickOutside)
    document.removeEventListener('keydown', this.#onKeydown)
  }

  openEdit (event) {
    event.stopPropagation()
    const anchor = event.currentTarget.closest('[data-initialstate-room-popover-target="anchor"]')
    if (!anchor) return

    this.roomIdTarget.value = anchor.dataset.roomId
    this.nameInputTarget.value = anchor.dataset.roomName || ''
    this.surfaceInputTarget.value = anchor.dataset.roomSurface || ''
    this.surfaceInputTarget.dispatchEvent(new Event('input'))
    this.clearErrors()
    this.deleteButtonTarget.classList.toggle('hidden', this.roomsCountValue <= 1)
    this.#open(anchor)
  }

  createRoom (event) {
    event.stopPropagation()
    this.#recut(this.#baseUrl(), 'POST')
  }

  submit (event) {
    event.preventDefault()
    this.clearErrors()

    const roomId = this.roomIdTarget.value
    const anchor = this.referenceElement
    if (!roomId || !anchor || !this.validateForm()) {
      return
    }
    if (!this.#tryLock()) return

    const next = {
      name: this.nameInputTarget.value.trim(),
      surface: parseFloat(this.surfaceInputTarget.value)
    }
    const previous = this.#chipState(anchor)
    this.#paintChip(anchor, next)
    this.close()
    this.#patchRoom(anchor, roomId, next, previous)
  }

  submitDelete (event) {
    event.preventDefault()

    const roomId = this.roomIdTarget.value
    if (!roomId) {
      this.close()
      return
    }

    this.close()
    this.#recut(`${this.#baseUrl()}/${roomId}`, 'DELETE')
  }

  deleteRoom (event) {
    event.stopPropagation()

    if (this.roomsCountValue <= 1) return

    const anchor = event.currentTarget.closest('[data-initialstate-room-popover-target="anchor"]')
    const roomId = anchor?.dataset?.roomId
    if (!roomId) return

    this.#recut(`${this.#baseUrl()}/${roomId}`, 'DELETE')
  }

  close () {
    this.panelTarget.hidden = true
    this.#teardownPositioning()
    document.removeEventListener('click', this.#onClickOutside)
    document.removeEventListener('keydown', this.#onKeydown)
    this.referenceElement = null
  }

  validateForm () {
    const name = this.nameInputTarget.value.trim()
    const surface = parseFloat(this.surfaceInputTarget.value)
    let isValid = true

    if (!name) {
      this.nameErrorTarget.textContent = this.nameRequiredValue
      isValid = false
    } else if (name.length > 255) {
      this.nameErrorTarget.textContent = this.nameTooLongValue
      isValid = false
    }

    if (!surface || surface <= 0) {
      this.surfaceErrorTarget.textContent = this.surfaceRequiredValue
      isValid = false
    }

    return isValid
  }

  clearErrors () {
    this.nameErrorTarget.textContent = ''
    this.surfaceErrorTarget.textContent = ''
  }

  swallow (event) {
    event.stopPropagation()
  }

  #baseUrl () {
    return `/simulation_requests/${this.simulationRequestSqidValue}/initialstate_rooms`
  }

  #csrfHeaders (accept) {
    return {
      Accept: accept,
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
    }
  }

  #patchRoom (anchor, roomId, next, previous) {
    this.#fetchWithTimeout(`${this.#baseUrl()}/${roomId}`, {
      method: 'PATCH',
      headers: this.#csrfHeaders(TURBO_STREAM),
      body: JSON.stringify({ name: next.name, surface_m2: next.surface })
    })
      .then(async (response) => {
        if (response.ok) {
          Turbo.renderStreamMessage(await response.text())
          return
        }

        this.#paintChip(anchor, previous)
        this.#open(anchor)

        if (response.status === 422) {
          const payload = await response.json()
          this.surfaceErrorTarget.textContent = payload.message
          return
        }

        this.surfaceErrorTarget.textContent = this.requestFailedValue
      })
      .catch(() => {
        this.#paintChip(anchor, previous)
        this.#open(anchor)
        this.surfaceErrorTarget.textContent = this.requestFailedValue
      })
      .finally(() => {
        this.#unlock()
      })
  }

  #recut (url, method) {
    if (!this.#tryLock()) return

    this.#clearRecutError()

    this.#fetchWithTimeout(url, {
      method,
      headers: this.#csrfHeaders(TURBO_STREAM)
    })
      .then(async (response) => {
        if (response.status === 422) {
          const payload = await response.json()
          this.#showRecutError(payload.message)
          return
        }

        if (!response.ok) throw new Error(`Request failed: ${response.status}`)

        Turbo.renderStreamMessage(await response.text())
      })
      .catch(() => {
        this.#showRecutError(this.requestFailedValue)
      })
      .finally(() => {
        this.#unlock()
      })
  }

  #fetchWithTimeout (url, options) {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)
    return fetch(url, { ...options, signal: controller.signal })
      .finally(() => clearTimeout(timeoutId))
  }

  #tryLock () {
    const wrapper = this.#mutexNode()
    if (this.#pending || wrapper?.getAttribute('aria-busy') === 'true') return false

    this.#pending = true
    this.#lockWrapper = wrapper
    wrapper?.setAttribute('aria-busy', 'true')
    return true
  }

  #unlock () {
    this.#pending = false
    this.#lockWrapper?.removeAttribute('aria-busy')
    this.#lockWrapper = null
  }

  #mutexNode () {
    return this.element.closest('[data-decomposition-mutex]')
  }

  #showRecutError (message) {
    this.recutErrorTarget.textContent = message
  }

  #clearRecutError () {
    this.recutErrorTarget.textContent = ''
  }

  #chipState (anchor) {
    return {
      name: anchor.dataset.roomName || '',
      surface: parseFloat(anchor.dataset.roomSurface)
    }
  }

  #paintChip (anchor, { name, surface }) {
    const formatted = this.#formatSurface(surface)
    anchor.dataset.roomName = name
    anchor.dataset.roomSurface = formatted
    const nameLabel = anchor.querySelector('[data-room-label]')
    const surfaceLabel = anchor.querySelector('[data-room-surface-label]')
    if (nameLabel) nameLabel.textContent = name
    if (surfaceLabel) surfaceLabel.textContent = `${formatted} m²`
  }

  #formatSurface (value) {
    return Number(value).toFixed(1).replace(/\.0$/, '')
  }

  #open (referenceElement) {
    this.referenceElement = referenceElement
    this.panelTarget.hidden = false
    this.#teardownPositioning()
    this.cleanupAutoUpdate = autoUpdate(
      this.referenceElement,
      this.panelTarget,
      () => this.#updatePosition()
    )
    this.#updatePosition()

    window.requestAnimationFrame(() => {
      document.addEventListener('click', this.#onClickOutside)
      document.addEventListener('keydown', this.#onKeydown)
      this.nameInputTarget.focus()
    })
  }

  #updatePosition () {
    if (!this.referenceElement || this.panelTarget.hidden) return

    computePosition(this.referenceElement, this.panelTarget, {
      strategy: 'fixed',
      placement: 'bottom-start',
      middleware: [
        offset(OFFSET_PX),
        flip(),
        shift({ padding: OFFSET_PX })
      ]
    }).then(({ x, y, strategy }) => {
      Object.assign(this.panelTarget.style, {
        position: strategy,
        left: `${x}px`,
        top: `${y}px`
      })
    })
  }

  #teardownPositioning () {
    if (this.cleanupAutoUpdate) {
      this.cleanupAutoUpdate()
      this.cleanupAutoUpdate = null
    }
  }

  #onClickOutside = (event) => {
    if (this.element.contains(event.target)) return
    this.close()
  }

  #onKeydown = (event) => {
    if (event.key === 'Escape') this.close()
  }
}
